// ============ WHICH PART OF THE KUBE'S TEXTURE A BIT WEARS ============
// A bit is an eighth of a kube by volume and half of it on every edge, seated in one of the 27 sub-cell lattice slots.
// It used to wear the WHOLE kube texture shrunk down, which is wrong in the same way a photograph of a wall is not a
// brick: the bit is a PIECE of that kube, so it should show the piece of the pattern that was in that part of the
// kube-space. This module is the mapping, and nothing else.
//
// PURE AND DEPENDENCY-FREE on purpose — no THREE, no DOM, no GL. Two very different consumers need the identical
// answer and would otherwise drift:
//   * the on-world voxel shader, which remaps vMapUv per instance for a whole bucket of bits (system-view.js), and
//   * the hotbar/inventory icon, which bakes the same window into a cloned box geometry (app.js).
// tests/bit-uv.test.mjs proves the properties below against THIS file, so neither consumer is checking its own work.
//
// ── THE FACE TABLE IS MEASURED, NOT ASSUMED ──────────────────────────────────────────────────────────────────────
// Everything here rests on how three.js lays UVs out on a BoxGeometry, which is a convention, not a law — and it is
// per-face, with two of the six faces running BACKWARDS. Guessing it gives a bit whose texture is mirrored on half
// the cube, which reads as "slightly wrong" rather than as a bug and would survive review. So it was measured: build
// a BoxGeometry in the shipped three (r184), group the vertices by face normal, and read off which object-space axis
// drives u and which drives v, with sign. The result:
//
//     face      u comes from      v comes from
//     +X        z  (NEGATED)      y
//     -X        z                 y
//     +Y        x                 z  (NEGATED)
//     -Y        x                 z
//     +Z        x                 y
//     -Z        x  (NEGATED)      y
//
// Re-measure and update FACES if the three version ever changes; tests/bit-uv.test.mjs pins the consequences, not the
// table, so a silent convention change would surface as a tiling failure rather than as nothing at all.
//
// ── ONE TABLE, TWO LANGUAGES ─────────────────────────────────────────────────────────────────────────────────────
// The GLSL the shader injects is GENERATED from the same table the JS reads (see bitUvGLSL). A hand-written shader
// twin is exactly the kind of copy that agrees with its source on the day it is written and quietly stops agreeing
// three commits later — this codebase has been bitten by hand-mirrored logic before.

export const BIT_UV_SPAN = 0.5;      // a bit covers half the cell on each axis, so half the texture on each face
export const AXIS = { x: 0, y: 1, z: 2 };

// [uAxis, uNegated, vAxis, vNegated], indexed by face: +X, -X, +Y, -Y, +Z, -Z.
export const FACES = [
  { n: [ 1, 0, 0], u: AXIS.z, uNeg: true,  v: AXIS.y, vNeg: false },
  { n: [-1, 0, 0], u: AXIS.z, uNeg: false, v: AXIS.y, vNeg: false },
  { n: [ 0, 1, 0], u: AXIS.x, uNeg: false, v: AXIS.z, vNeg: true  },
  { n: [ 0,-1, 0], u: AXIS.x, uNeg: false, v: AXIS.z, vNeg: false },
  { n: [ 0, 0, 1], u: AXIS.x, uNeg: false, v: AXIS.y, vNeg: false },
  { n: [ 0, 0,-1], u: AXIS.x, uNeg: true,  v: AXIS.y, vNeg: false },
];

// Which face a unit axis-aligned normal names, or -1 for anything else.
export function faceIndex(nx, ny, nz) {
  if (nx > 0.5) return 0; if (nx < -0.5) return 1;
  if (ny > 0.5) return 2; if (ny < -0.5) return 3;
  if (nz > 0.5) return 4; if (nz < -0.5) return 5;
  return -1;
}

// The bit's MIN CORNER inside its cell, in cell units, from the lattice offset (slotOffset gives the CENTRE).
// Lattice steps 0/1/2 → centres 0.25/0.5/0.75 → minima 0/0.25/0.5, so a bit always lies wholly inside its cell.
export function bitCellMin(offset) {
  const h = BIT_UV_SPAN * 0.5;
  return [offset[0] - h, offset[1] - h, offset[2] - h];
}

// The origin of the bit's texture window on one face. The window is always BIT_UV_SPAN square.
//
// The two cases are the whole content of this file. Where the face reads FORWARDS along an axis, texture coordinate
// and cell coordinate advance together, so a bit starting at `min` starts at `min` in the texture. Where the face
// reads BACKWARDS, the bit's FAR edge becomes the window's near edge, so the origin is `1 - span - min`.
//
// Note that `1 - span - min` and `span - min` are the same number today, because span is exactly 0.5 — half a kube.
// They are NOT the same rule, and the wrong one is invisible until BIT_SCALE changes, at which point every negated
// face (two of six) silently mirrors instead of shifting. The general form is written out for that reason.
export function bitUvOrigin(nx, ny, nz, min) {
  const f = faceIndex(nx, ny, nz);
  if (f < 0) return null;
  const F = FACES[f];
  const back = (m) => 1 - BIT_UV_SPAN - m;
  return [F.uNeg ? back(min[F.u]) : min[F.u],
          F.vNeg ? back(min[F.v]) : min[F.v]];
}

// Map one of the bit's own face UVs (0..1 across the bit) into the kube texture.
export function bitUv(nx, ny, nz, min, u, v) {
  const o = bitUvOrigin(nx, ny, nz, min);
  if (!o) return [u, v];
  return [o[0] + u * BIT_UV_SPAN, o[1] + v * BIT_UV_SPAN];
}

// THE ICON OCTANT. An item in a backpack occupies no lattice slot, so there is no "correct part" to show — but a bit
// that looks like a shrunken whole kube in the bar and like a cropped piece once placed reads as two different items.
// Icons therefore use one FIXED octant so the bar agrees with the world; the origin corner is the natural choice
// because it is the only one whose window is the identity on every non-negated face.
export const ICON_BIT_MIN = [0, 0, 0];

// The GLSL for the same mapping, GENERATED from FACES so the shader cannot drift from the JS above.
// `off` is the bit's min corner (a vec3 instanced attribute); rewrites `uvOut` in place.
export function bitUvGLSL(offExpr = 'aBitMin', uvExpr = 'vMapUv') {
  const comp = 'xyz';
  const back = (1 - BIT_UV_SPAN).toFixed(4).replace(/0+$/, '');   // the same `1 - span - min` the JS uses
  const term = (F, which) => {
    const neg = which === 'u' ? F.uNeg : F.vNeg;
    const a = comp[which === 'u' ? F.u : F.v];
    return neg ? `(${back} - ${offExpr}.${a})` : `${offExpr}.${a}`;
  };
  const arm = (F) => `vec2(${term(F, 'u')}, ${term(F, 'v')})`;
  return [
    '\tvec3 bn_ = normal;',
    `\tvec2 bo_ = ${arm(FACES[4])};`,                                  // +Z, also the fallback
    `\tif (bn_.x >  0.5) bo_ = ${arm(FACES[0])};`,
    `\telse if (bn_.x < -0.5) bo_ = ${arm(FACES[1])};`,
    `\telse if (bn_.y >  0.5) bo_ = ${arm(FACES[2])};`,
    `\telse if (bn_.y < -0.5) bo_ = ${arm(FACES[3])};`,
    `\telse if (bn_.z < -0.5) bo_ = ${arm(FACES[5])};`,
    `\t${uvExpr} = bo_ + ${uvExpr} * ${BIT_UV_SPAN.toFixed(1)};`,
  ].join('\n');
}
